import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import TicketDetails from "./TicketDetails";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import TicketManagementForm from "./TicketManagementForm";
import theme from "../theme";
import { useLocation } from "react-router-dom";
import TicketActivityTimelineCard from "./TicketActivityTimelineCard";
import TicketTimerButton from "./TicketTimerButton";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import axios from "axios";
import { STORE_ID } from "../Config/Constants";
import LoadingScreen from "./LoadingScreen";

import userIcon from "../assets/icons/user.svg";
import phoneIcon from "../assets/icons/customer-call.svg";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const TicketPanel = () => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  const location = useLocation();
  // A direct load or an F5 arrives with no router state at all — destructuring
  // it threw before anything could render, white-screening the page. Falling back
  // to an empty object leaves ticketID undefined, which the fetch below turns
  // into the error branch that already exists for a ticket that cannot be read.
  const {
    data = null,
    filteredTickets = null,
    index = null,
  } = location.state || {};
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const [ticketIndex, setTicketIndex] = useState(index);
  const store_id = userInfo?.store_id;
  const ticketID = filteredTickets
    ? filteredTickets[ticketIndex]?.id
    : data?.id;

  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleNextIndex = () => {
    if (ticketIndex < filteredTickets?.length - 1)
      setTicketIndex((prev) => prev + 1);
  };

  const handlePrevIndex = () => {
    if (ticketIndex > 0) setTicketIndex((prev) => prev - 1);
  };

  // State for tab
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const getTicketData = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getticketinfobyid?ticket_id=${ticketID}`,
        );

        if (data?.statusCode === 200 && data?.data?.ticket) {
          const ticketObj = {
            ...data.data.ticket,
            media: JSON.parse(data.data.ticket.media || "[]"),
          };
          setTicketData({
            ...data.data,
            ticket: ticketObj,
          });
        } else {
          setError(true);
        }
      } catch (error) {
        console.error("Error fetching ticket:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    getTicketData();
  }, [ticketID]);

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <LoadingScreen loadingMessage={"Loading ticket details"} />;
  }

  if (error || !ticketData) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography color="error">Something went wrong.</Typography>
      </Box>
    );
  }

  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
    });
    const formattedTime = date.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${formattedDate}, ${formattedTime}`;
  }

  return (
    <Box sx={{ bgcolor: "#fff", pb: 8, borderRadius: 3, mt: 1 }}>
      {/* Header */}
      <Box
        sx={{
          background:
            "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
          height: 140,
          width: "100%",
          // px: "16px",

          // boxShadow: "0 0px 4px gray",
          fontSize: 12,
          borderRadius: "12px 12px 0 0",
          zIndex: 1000,

          // top: "calc(56px + env(safe-area-inset-top, 24px))",
        }}
      >
        {/* Header Part */}
        <Box sx={{ px: 2, height: 140 }}>
          {/* Store ID | Store Name */}
          <Typography
            sx={{
              fontSize: 14,
              width: "100%",
              textAlign: "center",
              color: "#fff",
              fontWeight: 600,
              pt: 2,
            }}
          >
            {ticketData?.ticket.store_id} | {ticketData?.ticket.store_name}
          </Typography>

          {/* Horizontal Line */}
          <Box
            component="hr"
            sx={{ border: "none", borderTop: "2px solid #fff", my: 1 }}
          />

          {/* Department and Ticket Number */}
          <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
            {ticketData?.ticket.department_name} |{" "}
            <span style={{ fontSize: "12px", fontWeight: 400 }}>
              #{ticketData?.ticket.ticket_number}
            </span>
          </Typography>

          {/* Ticket Created Date & Customer Calling Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Ticket Created Date */}
            <Typography sx={{ fontSize: 12, color: "#fff", fontWeight: 300 }}>
              {formatDate(ticketData?.ticket.created_at)}
            </Typography>

            {/* Customer Calling Button */}
            {store_id === STORE_ID && (
              <Box
                sx={{
                  backgroundColor: ticketData?.ticket.customer_phone
                    ? theme.button.primaryBtn
                    : "#fff",
                  display: "flex",
                  gap: 1,
                  padding: "5px 15px",
                  borderRadius: "20px",
                  alignItems: "center",
                  cursor: ticketData?.ticket.customer_phone
                    ? "pointer"
                    : "not-allowed",
                }}
                onClick={() => {
                  if (!ticketData?.ticket.creator_mobile) return;
                  window.open(`tel:${ticketData.ticket.creator_mobile}`);
                }}
              >
                <img style={{ width: "20px" }} src={phoneIcon} alt="phone" />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    background:
                      "linear-gradient(90deg,rgba(0, 173, 173, 1) -100%, rgba(68, 40, 160, 1) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Customer Call
                </Typography>
              </Box>
            )}
          </Box>

          {/* Ticket Raised By */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <img src={userIcon} alt="user" width="22" height="22" />
            <Typography
              sx={{ fontSize: "12px", fontWeight: 300, color: "#fff" }}
            >
              {ticketData?.ticket.created_by_name}
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            bgcolor: "#ffff",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            textColor="inherit"
            variant="standard"
            TabIndicatorProps={{
              sx: {
                height: "4px",
                backgroundColor: "#008080",
                width: "fit-content",
                mx: "auto",
                borderRadius: "50px 50px 0 0",
              },
            }}
          >
            {(store_id === STORE_ID ||
              ticketData?.ticket?.type !== "external") && (
              <Tab
                label="Manage"
                sx={{
                  fontSize: 14,
                  textTransform: "none",
                  "&.Mui-selected": {
                    color: "#008080",
                    fontWeight: 600,
                  },
                }}
              />
            )}

            <Tab
              label="Timeline"
              sx={{
                fontSize: 14,
                textTransform: "none",
                "&.Mui-selected": {
                  color: "#008080",
                  fontWeight: 600,
                },
              }}
            />
          </Tabs>

          {filteredTickets && (
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 0 : 2,
                alignItems: "center",
              }}
            >
              {isMobile && (
                <ChevronLeftIcon
                  onClick={handlePrevIndex}
                  sx={{
                    fontSize: 28,
                    color: ticketIndex > 0 ? "rgba(0, 125, 125, 1)" : "gray",
                  }}
                />
              )}

              <Typography sx={{ fontSize: isMobile ? 12 : 14 }}>
                {ticketIndex + 1}/
                <span style={{ color: "rgba(0, 120, 120, 1)" }}>
                  {filteredTickets.length}
                </span>{" "}
              </Typography>
              {isMobile && (
                <ChevronRightIcon
                  onClick={handleNextIndex}
                  sx={{
                    fontSize: 28,
                    color:
                      ticketIndex < filteredTickets?.length - 1
                        ? "rgba(0, 120, 120, 1)"
                        : "gray",
                  }}
                />
              )}
              {!isMobile && (
                <Button
                  onClick={handlePrevIndex}
                  sx={{
                    cursor: "pointer",
                    fontSize: 12,
                    background:
                      ticketIndex > 0
                        ? "linear-gradient(90deg,rgba(0, 173, 173, 1) -100%, rgba(68, 40, 160, 1) 100%)"
                        : "gray",
                    color: "#fff",
                    px: 1.5,
                    borderRadius: 5,
                    py: 0.4,
                  }}
                >
                  Prev
                </Button>
              )}
              {!isMobile && (
                <Button
                  onClick={handleNextIndex}
                  sx={{
                    cursor: "pointer",
                    fontSize: 12,
                    background:
                      ticketIndex < filteredTickets?.length - 1
                        ? "linear-gradient(90deg,rgba(0, 173, 173, 1) -100%, rgba(68, 40, 160, 1) 100%)"
                        : "gray",
                    color: "#fff",
                    px: 1.5,
                    borderRadius: 5,
                    py: 0.4,
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Horizontal Line */}
        {/* <Box
          component="hr"
          sx={{ border: "none", borderTop: "2px solid #9E9E9E", my: 0 }}
        /> */}
      </Box>

      {/* Tab Panels */}
      <Box sx={{ mt: "60px", px: 2 }}>
        {/* Work timer — Start/Stop drives the auto-timesheet */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
            Work timer
          </Typography>
          <TicketTimerButton ticket={ticketData.ticket} />
        </Box>

        {/* Details */}
        {/* {activeTab === 0 && <TicketDetails ticketData={ticketData} />} */}

        {/* Manage */}
        {(store_id === STORE_ID || ticketData?.ticket?.type !== "external") &&
          activeTab === 0 && <TicketManagementForm ticketData={ticketData} />}

        {/* Timeline */}
        {activeTab ===
          (store_id === STORE_ID || ticketData?.ticket?.type !== "external"
            ? 1
            : 0) && (
          <TicketActivityTimelineCard
            ticketTimeline={ticketData.ticket_info}
            ticket={ticketData.ticket}
          />
        )}
      </Box>
    </Box>
  );
};

export default TicketPanel;
